import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import includes from 'lodash/includes'
import capitalize from 'lodash/capitalize'
import { getEmojiURL } from 'utils/emoji'
import ButtonBase from '@material-ui/core/ButtonBase'
import { withStyles } from '@material-ui/core/styles'
import { connectGetters } from 'react-redux-getters'
import { getPlans } from 'selectors/static'
import ContentLoader from 'atoms/ContentLoader'
import { AppProgressLoadable } from 'atoms/AppProgress'
import Card from 'atoms/Card'
import Typography from '@material-ui/core/Typography'
import Chip from 'atoms/Chip'
import DateTime from 'atoms/DateTime'
import Spacer from 'atoms/Spacer'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'

const styles = theme => ({
  plan: {
    padding: theme.spacing.unit * 2,
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: theme.transitions.create(['box-shadow']),
  },
  planSelected: {
    boxShadow: '0 6px 29px 0 rgba(103, 180, 255, 0.38)'
  },
  planPriceSign: {
    fontSize: theme.typography.caption.fontSize,
    verticalAlign: 'text-top',
  },
  planDescription: {
    fontSize: theme.typography.caption.fontSize,
    minHeight: 34,
  },
  planSummaryTitle: {
    fontSize: theme.typography.caption.fontSize,
  },
  planSummaryItem: {
    fontSize: theme.typography.caption.fontSize,
    display: 'flex',
    alignItems: 'flex-start',
    '&:not(:last-child)': {
      paddingBottom: theme.spacing.unit / 2,
    },
    '& span': {
      marginLeft: theme.spacing.unit,
    }
  },
  planButton: {
    borderRadius: 0,
  },
  planGrow: {
    flex: 1,
  },
  periodSwitch: {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
  },
  periodSwitchButton: {
    minWidth: '6rem',
    padding: theme.spacing.unit,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.default,
    transition: theme.transitions.create(['background-color', 'color'])
  },
  periodSwitchButtonActive: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  periodSwitchOr: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing.unit * 3,
    height: theme.spacing.unit * 3,
  }
})

const MONTHLY = 'monthly'
const ANNUAL = 'annual'

const mapGettersToProps = state => ({
  plansGetter: getPlans(state),
})

export default
@connectGetters(mapGettersToProps)
@withStyles(styles)
class BillingPlans extends Component {
  static propTypes = {
    selectedPlanId: PropTypes.string,
    classes: PropTypes.object.isRequired,
    plansGetter: PropTypes.object.isRequired,
    renderPlanButtonText: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    getPlanExpireDate: PropTypes.func,
  }

  static defaultProps = {
    getPlanExpireDate: (plan, period = MONTHLY) => {
      const { trial: expireDays } = plan.data[period]
      if (!expireDays) return null
      return moment().add(expireDays, 'days')
    }
  }

  state = {
    period: MONTHLY
  }

  renderPeriodSwitch() {
    const { classes } = this.props
    const { period: currentPeriod } = this.state
    return (
      <div className={classes.periodSwitch}>
        { [MONTHLY, ANNUAL].map(period => (
          <ButtonBase
            key={period}
            tabIndex="-1"
            onClick={() => this.setState({ period })}
            className={classNames(classes.periodSwitchButton, {
              [classes.periodSwitchButtonActive]: period === currentPeriod
            })}
          >
            <Typography color="inherit">
              { capitalize(period) }
            </Typography>
          </ButtonBase>
        )) }
        <Typography variant="caption" color="textSecondary" className={classes.periodSwitchOr}>
          or
        </Typography>
      </div>
    )
  }

  renderPlan = (plan, index) => {
    const {
      classes, selectedPlanId, renderPlanButtonText, onSelect, getPlanExpireDate
    } = this.props
    const { period } = this.state
    const expireDate = getPlanExpireDate(plan, period)
    const { id, price, period: periodLabel } = plan.data[period]
    const selected = includes([plan.data[ANNUAL].id, id], selectedPlanId)
    const cx = classNames(classes.plan, {
      [classes.planSelected]: selected
    })
    return (
      <Grid item key={plan.name} xs={12} sm={6} md={4}>
        <Card className={cx}>
          <Typography align="center" variant="h6">{ plan.name }</Typography>
          <Spacer small />
          <Typography align="center" variant="h6" color="primary">
            <sup className={classes.planPriceSign}>
              { price ? '$' : '\u00A0' }
            </sup>
            {' '}
            { price || '\u00A0' }
          </Typography>
          <Typography align="center" variant="caption" color="textSecondary">
            <i>{ periodLabel || <span>&nbsp;</span> }</i>
          </Typography>
          <Spacer />
          <Divider />
          <Spacer />
          <div className={classes.planGrow}>
            <Typography align="center" className={classes.planDescription}>
              { plan.description }
            </Typography>
            <Spacer />
            <Typography variant="subtitle2" className={classes.planSummaryTitle}>
              { plan.summary.title }
            </Typography>
            <Spacer small />
            { plan.summary.items.map(item => (
              <Typography key={item.text} className={classes.planSummaryItem}>
                { item.unified.map((unified, unifiedIndex) => (
                  // eslint-disable-next-line
                  <img key={unifiedIndex} alt="" src={getEmojiURL(unified)} width={16} height={16} />
                )) }
                <span>{ item.text }</span>
              </Typography>
            )) }
          </div>
          <Spacer />
          <Divider />
          <Spacer />
          <Chip
            fullWidth
            onClick={() => onSelect(id)}
            className={classes.planButton}
            color={selected ? 'primary' : 'secondary'}
            active={selected}
          >
            { renderPlanButtonText(plan, period, index) }
          </Chip>
          <Spacer small />
          { expireDate ? (
            <Typography variant="caption" align="center" color="textSecondary">
              {'free trial expires on '}
              <DateTime date={expireDate} dateOnly />
            </Typography>
          ) : (
            <Fragment>&nbsp;</Fragment>
          ) }
        </Card>
      </Grid>
    )
  }

  renderPlans() {
    const { plansGetter } = this.props
    return (
      <Grid container spacing={16}>
        { plansGetter.data.map(this.renderPlan) }
      </Grid>
    )
  }

  render() {
    const { plansGetter } = this.props
    return (
      <Fragment>
        <AppProgressLoadable loader={plansGetter} />
        { plansGetter.isPending && <ContentLoader /> }
        { plansGetter.isSucceded && (
          <Fragment>
            { this.renderPeriodSwitch() }
            <Spacer />
            { this.renderPlans() }
          </Fragment>
        ) }
      </Fragment>
    )
  }
}
